import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Phone, Youtube, ArrowRight, Sparkles } from 'lucide-react';
import Seo from '@/components/Seo';
import NoticeBanner from '@/components/NoticeBanner';

/**
 * Catálogo de prótesis capilares — Guía comparativa de nueve sistemas.
 * Consumes /app/frontend/src/data/hairSystems.js; images live in
 * /public/assets/catalogo-protesis/. Every "Comprar" link jumps to the
 * matching product page in the store; the top "Ver dossier" scrolls to the
 * detailed section rendered below the grid.
 */
import { hairSystems } from '@/data/hairSystems';

const catalogSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Catálogo de prótesis capilares',
  description:
    'Guía comparativa de nueve sistemas capilares Arthur Fitz: V-Loop, mallas, skin anudada, implantación inyectada y construcciones mixtas.',
};

export default function CatalogoProtesis({ user, cartCount }) {
  const navigate = useNavigate();

  const scrollToSystem = (slug) => {
    const el = document.getElementById(`sistema-${slug}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="catalogo-protesis-page">
      <Seo
        title="Catálogo Prótesis Capilares · Guía comparativa"
        description="Compara nueve sistemas capilares: V-Loop ultrafina y fina, malla francesa, malla suiza, skin anudada, prótesis inyectada y tres construcciones mixtas. Base, duración orientativa y prioridades de cada modelo."
        path="/catalogo-protesis"
        jsonLd={catalogSchema}
      />

      {/* Header (reusing the site chrome pattern for consistency) */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="brand-home-link">
              ARTHUR FITZ
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/productos" className="text-sm font-medium text-gray-700 hover:text-black">PRODUCTOS</Link>
              <Link to="/catalogo-protesis" className="text-sm font-medium text-black">CATÁLOGO</Link>
              <Link to="/contacto-personalizado" className="text-sm font-medium text-gray-700 hover:text-black">CONTACTO</Link>
              <a href="https://www.youtube.com/nomascalvos" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
              <button onClick={() => window.open('https://wa.me/34649471889', '_blank')} aria-label="WhatsApp">
                <Phone className="w-5 h-5" />
              </button>
              <Link to="/checkout" className="relative" aria-label="Cesta">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              {user && <Link to="/perfil" aria-label="Perfil"><User className="w-5 h-5" /></Link>}
            </nav>
          </div>
        </div>
      </header>

      <NoticeBanner />

      {/* Hero */}
      <section className="border-b border-gray-200" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="container mx-auto px-6 py-20 max-w-6xl grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">
              Guía de sistemas capilares
            </span>
            <h1
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-[1.05]"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="catalog-hero-title"
            >
              Nueve sistemas.<br />Una elección más personal.
            </h1>
            <p className="mt-6 text-base text-gray-700 max-w-xl">
              No existe una única prótesis ideal para todo el mundo. La mejor elección depende del acabado, la densidad,
              la ventilación, la rutina de cuidado y la duración que quieras priorizar.
            </p>
            <p className="mt-3 text-sm text-gray-500">Las duraciones indicadas son siempre orientativas.</p>
          </div>
          <div className="relative grid grid-cols-2 gap-2 sm:gap-3" data-testid="catalog-hero-transformation">
            <figure className="relative aspect-[4/5] overflow-hidden border-2 border-black bg-gray-100">
              <img
                src="/assets/catalogo-protesis/hero.transform.before.webp"
                alt="Retrato en primer plano antes de utilizar una prótesis capilar Arthur Fitz."
                className="w-full h-full object-cover"
                loading="eager"
                width="568"
                height="710"
              />
              <figcaption className="absolute top-3 left-3 bg-white/95 text-black text-[10px] font-bold tracking-[0.25em] uppercase px-2 py-1">
                Antes
              </figcaption>
            </figure>
            <figure className="relative aspect-[4/5] overflow-hidden border-2 border-black bg-gray-100">
              <img
                src="/assets/catalogo-protesis/hero.transform.after.webp"
                alt="Retrato en primer plano después de utilizar una prótesis capilar Arthur Fitz."
                className="w-full h-full object-cover"
                loading="eager"
                width="568"
                height="710"
              />
              <figcaption className="absolute top-3 left-3 bg-black text-white text-[10px] font-bold tracking-[0.25em] uppercase px-2 py-1">
                Después
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Comparativa rápida */}
      <section className="container mx-auto px-6 py-16 max-w-6xl">
        <div className="mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">Comparativa rápida</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-black" style={{ fontFamily: 'Playfair Display' }}>
            Encuentra tu punto de equilibrio.
          </h2>
        </div>
        <div className="overflow-x-auto border-2 border-black">
          <table className="w-full text-left text-sm" data-testid="catalog-comparison-table">
            <thead className="bg-black text-white">
              <tr>
                <th className="px-4 py-3 font-semibold min-w-[220px]">Sistema</th>
                <th className="px-4 py-3 font-semibold min-w-[220px]">Base</th>
                <th className="px-4 py-3 font-semibold min-w-[160px]">Duración orientativa</th>
                <th className="px-4 py-3 font-semibold min-w-[200px]">Prioridad</th>
              </tr>
            </thead>
            <tbody>
              {hairSystems.map((s, i) => (
                <tr key={s.slug} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <th className="px-4 py-3 font-semibold text-black">
                    <button
                      onClick={() => scrollToSystem(s.slug)}
                      className="underline underline-offset-4 hover:text-gray-700 text-left"
                      data-testid={`catalog-row-${s.slug}`}
                    >
                      {s.name}
                    </button>
                  </th>
                  <td className="px-4 py-3 text-gray-700">{s.base}</td>
                  <td className="px-4 py-3 text-gray-700">{s.duration}</td>
                  <td className="px-4 py-3 text-gray-700">{s.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-gray-500 max-w-4xl">
          Las duraciones son estimaciones basadas en las medias observadas en las ventas de los últimos años. La vida útil
          real puede variar por el trato, la frecuencia y forma de uso, la exposición solar y al calor, la sudoración,
          los adhesivos, los productos químicos, la limpieza, el mantenimiento, la retirada y las características personales.
          No representan una garantía.
        </p>
      </section>

      {/* Grid de fichas */}
      <section className="py-16 border-t border-gray-200" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">Los nueve modelos</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-black" style={{ fontFamily: 'Playfair Display' }}>
              Elige por prioridad, no por popularidad.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hairSystems.map((s, index) => (
              <article
                key={s.slug}
                className="bg-white border-2 border-black flex flex-col"
                data-testid={`catalog-card-${s.slug}`}
              >
                <button
                  type="button"
                  onClick={() => scrollToSystem(s.slug)}
                  className="relative block aspect-[4/3] overflow-hidden bg-gray-100 border-b-2 border-black text-left"
                  aria-label={`Ver detalle de ${s.name}`}
                >
                  <img
                    src={s.heroImage}
                    alt={s.heroAlt}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                    loading="lazy"
                    width="1200"
                    height="900"
                  />
                  <span className="absolute top-3 left-3 bg-black text-white text-xs font-bold tracking-widest px-2 py-1">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </button>
                <div className="p-5 flex-1 flex flex-col">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">{s.family}</span>
                  <h3 className="mt-1 text-xl font-bold text-black" style={{ fontFamily: 'Playfair Display' }}>
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed">{s.summary}</p>
                  <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <dt className="uppercase tracking-wider text-gray-500">Duración</dt>
                      <dd className="mt-0.5 font-semibold text-black">{s.duration}</dd>
                    </div>
                    <div>
                      <dt className="uppercase tracking-wider text-gray-500">Prioriza</dt>
                      <dd className="mt-0.5 font-semibold text-black">{s.priority}</dd>
                    </div>
                  </dl>
                  <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                      onClick={() => scrollToSystem(s.slug)}
                      className="text-sm font-medium text-black underline underline-offset-4 hover:text-gray-700"
                      data-testid={`catalog-view-dossier-${s.slug}`}
                    >
                      Ver dossier
                    </button>
                    <button
                      onClick={() => navigate(`/producto/${s.productId}`)}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-black hover:text-gray-700"
                      data-testid={`catalog-buy-${s.slug}`}
                    >
                      Comprar <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Dossiers detallados */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-600">Dossiers detallados</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-black" style={{ fontFamily: 'Playfair Display' }}>
              Cada sistema, mirado de cerca.
            </h2>
          </div>

          <div className="space-y-16">
            {hairSystems.map((s) => (
              <article
                key={s.slug}
                id={`sistema-${s.slug}`}
                className="scroll-mt-24"
                data-testid={`dossier-${s.slug}`}
              >
                <div className="grid md:grid-cols-5 gap-8 items-start">
                  <div className="md:col-span-2">
                    <div className="aspect-[4/3] overflow-hidden border-2 border-black bg-gray-100">
                      <img src={s.heroImage} alt={s.heroAlt} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">{s.family}</span>
                    <h3 className="mt-2 text-2xl sm:text-3xl font-bold text-black" style={{ fontFamily: 'Playfair Display' }}>
                      {s.name}
                    </h3>
                    <p className="mt-2 text-base text-gray-800 italic">{s.tagline}</p>
                    <p className="mt-4 text-sm text-gray-700 leading-relaxed">{s.summary}</p>

                    <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 border-y border-gray-200 py-4">
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Base</dt>
                        <dd className="mt-1 text-sm font-medium text-black">{s.base}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Duración</dt>
                        <dd className="mt-1 text-sm font-medium text-black">{s.duration}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Densidad</dt>
                        <dd className="mt-1 text-sm font-medium text-black">{s.density}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Prioriza</dt>
                        <dd className="mt-1 text-sm font-medium text-black">{s.priority}</dd>
                      </div>
                    </dl>

                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                      <div>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Ideal para</span>
                        <p className="mt-1 text-sm text-gray-700">{s.idealFor}</p>
                      </div>
                      <div>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Cuidado</span>
                        <p className="mt-1 text-sm text-gray-700">{s.maintenance}</p>
                      </div>
                    </div>

                    <ul className="mt-6 space-y-3">
                      {s.strengths.map(([title, text]) => (
                        <li key={title} className="flex gap-3">
                          <Sparkles className="w-4 h-4 mt-0.5 text-black shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-black">{title}</p>
                            <p className="text-sm text-gray-700">{text}</p>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <p className="mt-6 text-xs text-gray-500 border-l-2 border-gray-300 pl-3 italic">
                      {s.consideration}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        onClick={() => navigate(`/producto/${s.productId}`)}
                        className="inline-flex items-center gap-2 border-2 border-black bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-black transition-colors"
                        data-testid={`dossier-buy-${s.slug}`}
                      >
                        Comprar {s.name} <ArrowRight className="w-4 h-4" />
                      </button>
                      <Link
                        to="/contacto-personalizado"
                        className="inline-flex items-center gap-2 border-2 border-black bg-white text-black px-6 py-3 text-sm font-semibold hover:bg-black hover:text-white transition-colors"
                      >
                        Consultar con un experto
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Galería completa del dossier — todas las tomas de esta variedad */}
                {Array.isArray(s.gallery) && s.gallery.length > 0 && (
                  <div className="mt-10" data-testid={`dossier-gallery-${s.slug}`}>
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">
                        La pieza, de cerca
                      </span>
                      <span className="text-xs text-gray-500">{s.gallery.length} vistas</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                      {s.gallery.map((g, gi) => (
                        <figure key={g.src} className="border-2 border-black bg-white flex flex-col">
                          <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                            <img
                              src={g.src}
                              alt={g.alt}
                              className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                              loading="lazy"
                              width="900"
                              height="675"
                              data-testid={`dossier-${s.slug}-photo-${gi}`}
                            />
                          </div>
                          <figcaption className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-700 border-t border-gray-200">
                            {g.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer (existing site style) */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <img
            src="/assets/brand/arthur-fitz-logo.png"
            alt="Arthur Fitz — Prótesis Capilares"
            width="80"
            height="80"
            loading="lazy"
            className="w-20 h-20 mx-auto mb-3 bg-white rounded-full p-1"
            data-testid="catalog-footer-brand-logo"
          />
          <p className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</p>
          <p className="text-sm text-gray-400">Prótesis capilares artesanales · Información orientativa · Cada persona es única.</p>
        </div>
      </footer>
    </div>
  );
}
